import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';

const RegisterRentalLayer = () => {
    const [rentals, setRentals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Add/Edit modal state
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingRentalId, setEditingRentalId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [formData, setFormData] = useState({
        tenant_names: '',
        sn: '',
        district: '',
        length_with: '',
        meter_square: '',
        m2price: '',
        proposed_rent_per_month: '',
        current_rent_per_month: '',
        status_of_agreement: 'Signed'
    });

    useEffect(() => {
        fetchRentals();
    }, []);

    const fetchRentals = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/rentals`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch rentals: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setRentals(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch rentals');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            tenant_names: '',
            sn: '',
            district: '',
            length_with: '',
            meter_square: '',
            m2price: '',
            proposed_rent_per_month: '',
            current_rent_per_month: '',
            status_of_agreement: 'Signed'
        });
        setValidationErrors({});
        setIsEditMode(false);
        setEditingRentalId(null);
    };

    const handleOpenAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const handleOpenEditModal = (rental) => {
        setFormData({
            tenant_names: rental.tenant_names || '',
            sn: rental.sn || '',
            district: rental.district || '',
            length_with: rental.length_with || '',
            meter_square: rental.meter_square || '',
            m2price: rental.m2price || '',
            proposed_rent_per_month: rental.proposed_rent_per_month || '',
            current_rent_per_month: rental.current_rent_per_month || '',
            status_of_agreement: rental.status_of_agreement || 'Signed'
        });
        setEditingRentalId(rental.id);
        setIsEditMode(true);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setIsSubmitting(true);
            setValidationErrors({});
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const url = isEditMode 
                ? `${apiUrl}/rentals/${editingRentalId}`
                : `${apiUrl}/rentals`;

            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setValidationErrors(data.errors);
                }
                throw new Error(data.message || `Failed to ${isEditMode ? 'update' : 'register'} rental`);
            }

            if (data.success) {
                handleCloseModal();
                fetchRentals();
                alert(data.message || `Rental ${isEditMode ? 'updated' : 'registered'} successfully!`);
            } else {
                throw new Error(data.message || `Failed to ${isEditMode ? 'update' : 'register'} rental`);
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (rentalId, tenantName) => {
        if (!window.confirm(`Are you sure you want to delete the rental for "${tenantName}"?`)) {
            return;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/rentals/${rentalId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                fetchRentals();
                alert('Rental deleted successfully!');
            } else {
                throw new Error(data.message || 'Failed to delete rental');
            }
        } catch (err) {
            console.error(err);
            alert(err instanceof Error ? err.message : 'Failed to delete rental');
        }
    };

    const filteredRentals = rentals.filter(rental =>
        rental.tenant_names.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (rental.sn && rental.sn.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (rental.district && rental.district.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className='card h-100 p-0 radius-12'>
            <div className='card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between'>
                <div className='d-flex align-items-center flex-wrap gap-3'>
                    <span className='text-md fw-medium text-secondary-light mb-0'>Show</span>
                    <select className='form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px'>
                        <option value='10'>10</option>
                        <option value='25'>25</option>
                        <option value='50'>50</option>
                    </select>
                    <form className='navbar-search'>
                        <input
                            type='text'
                            className='bg-base h-40-px w-auto'
                            name='search'
                            placeholder='Search'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Icon icon='ion:search-outline' className='icon' />
                    </form>
                </div>
                <button
                    type='button'
                    className='btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2'
                    onClick={handleOpenAddModal}
                >
                    <Icon
                        icon='ic:baseline-plus'
                        className='icon text-xl line-height-1'
                    />
                    Add New Rental
                </button>
            </div>
            <div className='card-body p-24'>
                {error && (
                    <div className='alert alert-danger mb-3' role='alert'>
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className='text-center py-5'>
                        <div className='spinner-border text-primary-600' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className='table-responsive scroll-sm'>
                        <table className='table bordered-table sm-table mb-0'>
                            <thead>
                                <tr>
                                    <th scope='col'>No</th>
                                    <th scope='col'>Tenant Names</th>
                                    <th scope='col'>S/N</th>
                                    <th scope='col'>District</th>
                                    <th scope='col'>Length With</th>
                                    <th scope='col'>Meter Square</th>
                                    <th scope='col'>M2price</th>
                                    <th scope='col'>Proposed Rent Per Month</th>
                                    <th scope='col'>Current Rent per Month</th>
                                    <th scope='col'>Status of the agreement</th>
                                    <th scope='col' className='text-center'>
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRentals.length === 0 ? (
                                    <tr>
                                        <td colSpan='11' className='text-center py-4'>
                                            No rentals found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRentals.map((rental, index) => (
                                        <tr key={rental.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <div className='d-flex align-items-center'>
                                                    <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                        {rental.tenant_names}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>{rental.sn || 'N/A'}</td>
                                            <td>{rental.district || 'N/A'}</td>
                                            <td>{rental.length_with || 'N/A'}</td>
                                            <td>{rental.meter_square || 'N/A'}</td>
                                            <td>{rental.m2price ? `$${rental.m2price}` : 'N/A'}</td>
                                            <td>{rental.proposed_rent_per_month ? `$${rental.proposed_rent_per_month}` : 'N/A'}</td>
                                            <td>{rental.current_rent_per_month ? `$${rental.current_rent_per_month}` : 'N/A'}</td>
                                            <td>
                                                <span className='bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm'>
                                                    {rental.status_of_agreement}
                                                </span>
                                            </td>
                                            <td className='text-center'>
                                                <div className='d-flex align-items-center gap-10 justify-content-center'>
                                                    <button
                                                        type='button'
                                                        className='bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => handleOpenEditModal(rental)}
                                                    >
                                                        <Icon
                                                            icon='lucide:edit'
                                                            className='menu-icon'
                                                        />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => handleDelete(rental.id, rental.tenant_names)}
                                                    >
                                                        <Icon
                                                            icon='fluent:delete-24-regular'
                                                            className='menu-icon'
                                                        />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className='modal-dialog modal-lg modal-dialog-centered'>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h5 className='modal-title'>{isEditMode ? 'Edit Rental' : 'Register New Rental'}</h5>
                                <button
                                    type='button'
                                    className='btn-close'
                                    onClick={handleCloseModal}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className='modal-body'>
                                    {error && (
                                        <div className='alert alert-danger mb-3' role='alert'>
                                            {error}
                                        </div>
                                    )}
                                    <div className='row gy-3'>
                                        <div className='col-12'>
                                            <label className='form-label'>Tenant Names <span className='text-danger'>*</span></label>
                                            <input
                                                type='text'
                                                name='tenant_names'
                                                className={`form-control ${validationErrors.tenant_names ? 'is-invalid' : ''}`}
                                                placeholder='Enter tenant names'
                                                value={formData.tenant_names}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.tenant_names && (
                                                <div className='invalid-feedback d-block'>
                                                    {validationErrors.tenant_names[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>S/N</label>
                                            <input
                                                type='text'
                                                name='sn'
                                                className='form-control'
                                                placeholder='Enter S/N'
                                                value={formData.sn}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>District</label>
                                            <select
                                                name='district'
                                                className='form-select'
                                                value={formData.district}
                                                onChange={handleInputChange}
                                            >
                                                <option value=''>Select District...</option>
                                                <option value='Abdiaziz'>Abdiaziz</option>
                                                <option value='Bondhere'>Bondhere</option>
                                                <option value='Daynile'>Daynile</option>
                                                <option value='Dharkenley'>Dharkenley</option>
                                                <option value='Hamar-Jajab'>Hamar-Jajab</option>
                                                <option value='Hamar-Weyne'>Hamar-Weyne</option>
                                                <option value='Hodan'>Hodan</option>
                                                <option value='Howl-Wadag'>Howl-Wadag</option>
                                                <option value='Huriwa'>Huriwa</option>
                                                <option value='Kaxda'>Kaxda</option>
                                                <option value='Karan'>Karan</option>
                                                <option value='Shangani'>Shangani</option>
                                                <option value='Shibis'>Shibis</option>
                                                <option value='Waberi'>Waberi</option>
                                                <option value='Wadajir'>Wadajir</option>
                                                <option value='Warta Nabada'>Warta Nabada</option>
                                                <option value='Yaqshid'>Yaqshid</option>
                                                <option value='Darusalam'>Darusalam</option>
                                                <option value='Garasbaley'>Garasbaley</option>
                                                <option value='Gubadley'>Gubadley</option>
                                            </select>
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>Length With</label>
                                            <input
                                                type='text'
                                                name='length_with'
                                                className='form-control'
                                                placeholder='Enter dimensions'
                                                value={formData.length_with}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>Meter Square</label>
                                            <input
                                                type='number'
                                                step='0.01'
                                                name='meter_square'
                                                className='form-control'
                                                placeholder='Enter meter square'
                                                value={formData.meter_square}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>M2 Price</label>
                                            <input
                                                type='number'
                                                step='0.01'
                                                name='m2price'
                                                className='form-control'
                                                placeholder='Enter M2 price'
                                                value={formData.m2price}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>Proposed Rent Per Month</label>
                                            <input
                                                type='number'
                                                step='0.01'
                                                name='proposed_rent_per_month'
                                                className='form-control'
                                                placeholder='Enter proposed rent'
                                                value={formData.proposed_rent_per_month}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>Current Rent Per Month</label>
                                            <input
                                                type='number'
                                                step='0.01'
                                                name='current_rent_per_month'
                                                className='form-control'
                                                placeholder='Enter current rent'
                                                value={formData.current_rent_per_month}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-md-6'>
                                            <label className='form-label'>Status of Agreement</label>
                                            <select
                                                name='status_of_agreement'
                                                className='form-select'
                                                value={formData.status_of_agreement}
                                                onChange={handleInputChange}
                                            >
                                                <option value='Signed'>Signed</option>
                                                <option value='Not-Signed'>Not-Signed</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className='modal-footer'>
                                    <button
                                        type='button'
                                        className='btn btn-secondary'
                                        onClick={handleCloseModal}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='submit'
                                        className='btn btn-primary'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Rental' : 'Register Rental')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterRentalLayer;

